import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { clearToken, getToken, setToken } from '../servicos/api';
import { usePerfilContexto } from './PerfilContexto';

export type UsuarioAuth = {
  id: number;
  email: string;
  nome?: string | null;
  sexo?: string;
  telefone?: string | null;
  subscriptionId?: string | null;
  subscriptionStatus?: string;
};

export type ResultadoAuth = { ok: boolean; codigo?: string; mensagem?: string };

type AuthContextType = {
  usuario: UsuarioAuth | null;
  autenticado: boolean;
  carregando: boolean; // bootstrap inicial da sessão
  processando: boolean; // ação em andamento (login/cadastro/etc.)
  entrar: (email: string, senha: string) => Promise<ResultadoAuth>;
  cadastrar: (email: string, senha: string, nome?: string) => Promise<ResultadoAuth>;
  esqueciSenha: (email: string) => Promise<ResultadoAuth>;
  definirSenha: (token: string, senha: string) => Promise<ResultadoAuth>;
  atualizarUsuario: (parcial: Partial<UsuarioAuth>) => void;
  sair: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

function tokenValido(token: string | null): boolean {
  if (!token) return false;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' && Date.now() < payload.exp * 1000;
  } catch {
    return false;
  }
}

function traduzErro(err: unknown): ResultadoAuth {
  const data = (err as { response?: { data?: { title?: string; detail?: string } } })?.response?.data;
  return {
    ok: false,
    codigo: data?.title, // ex.: "senha_nao_definida", "nao_autorizado", "validacao"
    mensagem: data?.detail || 'Não foi possível concluir. Tente novamente.'
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioAuth | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);
  const navigate = useNavigate();
  const { setPerfil } = usePerfilContexto();

  // Espelha o usuário autenticado no PerfilContexto legado (que várias telas ainda
  // consomem), disparando o carregamento inicial de contas/categorias/cartões.
  useEffect(() => {
    if (usuario) {
      setPerfil({
        id: usuario.id,
        email: usuario.email,
        nome: usuario.nome ?? undefined,
        telefone: usuario.telefone ?? undefined,
        sexo: usuario.sexo,
        subscriptionStatus: usuario.subscriptionStatus,
        carregarContas: true,
        carregarCategorias: true,
        carregarCartoes: true
      });
    } else {
      setPerfil({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario]);

  // Bootstrap único da sessão: valida o token e carrega o usuário uma vez.
  useEffect(() => {
    let ativo = true;
    (async () => {
      const token = getToken();
      if (!tokenValido(token)) {
        clearToken();
        if (ativo) setCarregando(false);
        return;
      }
      try {
        const { data } = await api.get<UsuarioAuth>('/usuarios/me');
        if (ativo) setUsuario(data);
      } catch {
        clearToken();
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const aplicarSessao = useCallback((data: { token: string; usuario: UsuarioAuth }) => {
    setToken(data.token);
    setUsuario(data.usuario);
  }, []);

  const entrar = useCallback(
    async (email: string, senha: string): Promise<ResultadoAuth> => {
      setProcessando(true);
      try {
        const { data } = await api.post('/auth/login', { email, senha });
        aplicarSessao(data);
        navigate('/dashboard');
        return { ok: true };
      } catch (err) {
        return traduzErro(err);
      } finally {
        setProcessando(false);
      }
    },
    [aplicarSessao, navigate]
  );

  const cadastrar = useCallback(
    async (email: string, senha: string, nome?: string): Promise<ResultadoAuth> => {
      setProcessando(true);
      try {
        const { data } = await api.post('/auth/registrar', { email, senha, nome });
        aplicarSessao(data);
        navigate('/dashboard');
        return { ok: true };
      } catch (err) {
        return traduzErro(err);
      } finally {
        setProcessando(false);
      }
    },
    [aplicarSessao, navigate]
  );

  const esqueciSenha = useCallback(async (email: string): Promise<ResultadoAuth> => {
    setProcessando(true);
    try {
      const { data } = await api.post('/auth/esqueci-senha', { email });
      return { ok: true, mensagem: data?.mensagem };
    } catch (err) {
      return traduzErro(err);
    } finally {
      setProcessando(false);
    }
  }, []);

  const definirSenha = useCallback(
    async (token: string, senha: string): Promise<ResultadoAuth> => {
      setProcessando(true);
      try {
        const { data } = await api.post('/auth/definir-senha', { token, senha });
        aplicarSessao(data);
        navigate('/dashboard');
        return { ok: true };
      } catch (err) {
        return traduzErro(err);
      } finally {
        setProcessando(false);
      }
    },
    [aplicarSessao, navigate]
  );

  const atualizarUsuario = useCallback((parcial: Partial<UsuarioAuth>) => {
    setUsuario((atual) => (atual ? { ...atual, ...parcial } : atual));
  }, []);

  const sair = useCallback(() => {
    clearToken();
    setUsuario(null);
    navigate('/');
  }, [navigate]);

  const value = useMemo<AuthContextType>(
    () => ({
      usuario,
      autenticado: !!usuario,
      carregando,
      processando,
      entrar,
      cadastrar,
      esqueciSenha,
      definirSenha,
      atualizarUsuario,
      sair
    }),
    [usuario, carregando, processando, entrar, cadastrar, esqueciSenha, definirSenha, atualizarUsuario, sair]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>.');
  return ctx;
}
